import React, { useState, useEffect, useRef } from 'react';
import api from '../services/authService';
import { BACKEND_URL } from '../config/env';
import {
  Folder,
  FolderOpen,
  FileText,
  UploadCloud,
  FolderPlus,
  RefreshCw,
  Trash2,
  Edit2,
  Download,
  Eye,
  ChevronRight,
  ChevronDown,
  ArrowLeft,
  Search,
  Grid,
  List,
  Server,
  CheckCircle2,
  AlertCircle,
  X,
  Send,
  Activity,
  Layers,
  HardDrive,
  ShieldCheck,
  Globe,
  Key,
  Lock,
  Unlock,
  Check,
  ServerCrash,
  Loader2,
  CheckSquare,
  Square,
  Filter,
  Building2,
  Zap
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API_BASE = '/bibliotheque-de-ressources';

// ── Ultra High-Fidelity macOS Sequoia Folder Icon SVG (Subtle & Elegant Apple Opening) ──
const MacFolderIcon = ({ size = 68, isOpen = false }) => (
  <svg
    width={size}
    height={size * 0.84}
    viewBox="0 0 84 70"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`drop-shadow-sm transition-transform duration-200 pointer-events-none ${
      isOpen ? 'scale-105 drop-shadow-md' : 'group-hover:scale-105 group-hover:drop-shadow-md'
    }`}
    style={{ overflow: 'visible' }}
  >
    <defs>
      {/* Back Plate Gradient */}
      <linearGradient id="macBackGrad" x1="0" y1="0" x2="0" y2="70" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#0284c7" />
        <stop offset="100%" stopColor="#0369a1" />
      </linearGradient>

      {/* Front Flap Gradient */}
      <linearGradient id="macFrontGrad" x1="0" y1="18" x2="0" y2="70" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#38bdf8" />
        <stop offset="60%" stopColor="#0ea5e9" />
        <stop offset="100%" stopColor="#0284c7" />
      </linearGradient>

      {/* Subtle Inner Pocket Shadow */}
      <linearGradient id="macShadowGrad" x1="0" y1="20" x2="0" y2="35" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#000000" stopOpacity="0.18" />
        <stop offset="100%" stopColor="#000000" stopOpacity="0" />
      </linearGradient>
    </defs>

    {/* Folder Back Plate with Tab */}
    <path
      d="M6 14C6 9.58172 9.58172 6 14 6H32C34.5 6 36.8 7.6 38.5 10L42.5 15.5C43.5 16.8 45.2 17.5 46.8 17.5H70C74.4183 17.5 78 21.0817 78 25.5V56C78 60.4183 74.4183 64 70 64H14C9.58172 64 6 60.4183 6 56V14Z"
      fill="url(#macBackGrad)"
    />

    {/* Folder Back Interior Lip Highlight */}
    <path
      d="M14 8H31.5C33.5 8 35.5 9.4 37 11.4L41 16.8C42.2 18.4 44.2 19.5 46.2 19.5H69.5C72.5 19.5 75 22 75 25"
      stroke="white"
      strokeOpacity="0.35"
      strokeWidth="1.2"
      strokeLinecap="round"
    />

    {/* Inner Document Paper Sheet subtly peeking out by 4px on drag-over */}
    <g
      className="transition-all duration-200"
      style={{
        transform: isOpen ? 'translateY(-4px)' : 'translateY(8px)',
        opacity: isOpen ? 0.95 : 0,
        transformOrigin: '42px 30px'
      }}
    >
      <rect x="22" y="14" width="40" height="24" rx="2.5" fill="#ffffff" stroke="#cbd5e1" strokeWidth="0.8" />
      <line x1="28" y1="19" x2="40" y2="19" stroke="#0284c7" strokeWidth="1.8" strokeLinecap="round" />
      <line x1="28" y1="24" x2="54" y2="24" stroke="#94a3b8" strokeWidth="1.2" strokeLinecap="round" />
      <line x1="28" y1="28" x2="48" y2="28" stroke="#cbd5e1" strokeWidth="1.2" strokeLinecap="round" />
    </g>

    {/* Front Flap Pocket - Tilts slightly down (just 3-4px) when isOpen is true */}
    <g
      className="transition-all duration-200"
      style={{
        transform: isOpen ? 'scaleY(0.92) translateY(4px)' : 'scaleY(1) translateY(0px)',
        transformOrigin: '42px 64px'
      }}
    >
      <path
        d="M6 28C6 23.5817 9.58172 20 14 20H70C74.4183 20 78 23.5817 78 28V56C78 60.4183 74.4183 64 70 64H14C9.58172 64 6 60.4183 6 56V28Z"
        fill="url(#macFrontGrad)"
      />
      {/* Inner Pocket Ambient Shadow */}
      <rect x="6" y="20" width="72" height="15" rx="6" fill="url(#macShadowGrad)" />
      {/* Top Rim Gloss Highlight */}
      <path
        d="M8 22C8 20.5 9.5 19.5 11 19.5H73C74.5 19.5 76 20.5 76 22C76 23 74.5 24 73 24H11C9.5 24 8 23 8 22Z"
        fill="white"
        fillOpacity="0.22"
      />
    </g>

    {/* Bottom Emboss Curve */}
    <path
      d="M9 62H75C76 62 77 61 77 60V56C77 59.5 74 62.5 70.5 62.5H13.5C10 62.5 7 59.5 7 56V60C7 61 8 62 9 62Z"
      fill="#0369a1"
      fillOpacity="0.4"
    />
  </svg>
);

// ── Ultra High-Fidelity macOS Document Icon SVG ──
const MacDocIcon = ({ ext = 'pdf', size = 56 }) => (
  <svg
    width={size}
    height={size * 1.22}
    viewBox="0 0 60 74"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="drop-shadow-sm transition-all duration-200 group-hover:scale-105 group-hover:drop-shadow-md pointer-events-none"
  >
    <defs>
      <linearGradient id="macDocGrad" x1="0" y1="0" x2="0" y2="74" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="100%" stopColor="#f8fafc" />
      </linearGradient>
    </defs>

    {/* Document Body */}
    <path
      d="M6 10C6 5.58172 9.58172 2 14 2H40L54 16V64C54 68.4183 50.4183 72 46 72H14C9.58172 72 6 68.4183 6 64V10Z"
      fill="url(#macDocGrad)"
      stroke="#cbd5e1"
      strokeWidth="1.5"
    />

    {/* Folded Corner */}
    <path
      d="M40 2V14C40 15.1046 40.8954 16 42 16H54"
      fill="#e2e8f0"
      stroke="#cbd5e1"
      strokeWidth="1.5"
    />

    {/* Extension Badge */}
    <rect
      x="8"
      y="35"
      width="44"
      height="20"
      rx="5"
      fill={
        ext === 'pdf'
          ? '#ef4444'
          : ['doc', 'docx'].includes(ext)
          ? '#2563eb'
          : ['xls', 'xlsx'].includes(ext)
          ? '#10b981'
          : '#64748b'
      }
    />
    <text
      x="30"
      y="49"
      fill="white"
      fontSize="10"
      fontWeight="900"
      textAnchor="middle"
      fontFamily="system-ui, -apple-system, sans-serif"
    >
      {ext.toUpperCase()}
    </text>
  </svg>
);

// ── Recursive Tree Node Component for Left Navigation Pane ──
const TreeNode = ({
  node,
  currentPath,
  onSelectPath,
  expandedPaths,
  toggleExpand,
  onDropOnFolder,
  onDragOverNode,
  onDragLeaveNode,
  dragOverFolder
}) => {
  const isExpanded = !!expandedPaths[node.path];
  const isSelected = currentPath === node.path;
  const isDragOver = dragOverFolder === node.path;
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div className="select-none text-xs relative">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDragOverNode(e, node);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDragLeaveNode(e);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDropOnFolder(node);
        }}
        onClick={() => onSelectPath(node.path)}
        className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg cursor-pointer transition-colors group relative ${
          isSelected
            ? 'bg-blue-50 text-blue-700 font-bold border border-blue-200/60 shadow-2xs'
            : isDragOver
            ? 'text-blue-600 font-bold'
            : 'text-slate-700 hover:bg-slate-100/80 font-medium'
        }`}
      >
        {/* Expand / Collapse Arrow */}
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand(node.path);
            }}
            className="p-0.5 rounded hover:bg-slate-200/70 text-slate-400 hover:text-slate-700 transition shrink-0"
          >
            {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          </button>
        ) : (
          <span className="w-4 shrink-0" />
        )}

        {/* Folder Icon (Opens on Drag-Over) */}
        {isExpanded || isDragOver ? (
          <FolderOpen size={15} className="text-sky-500 shrink-0 pointer-events-none" />
        ) : (
          <Folder size={15} className="text-sky-500 fill-sky-400/20 shrink-0 pointer-events-none" />
        )}

        {/* Name */}
        <span className="truncate text-xs pointer-events-none">{node.name}</span>
      </div>

      {/* Render Sub-nodes */}
      {hasChildren && isExpanded && (
        <div className="pl-4 ml-2 border-l border-slate-200/80 space-y-0.5 mt-0.5">
          {node.children.map((child) => (
            <TreeNode
              key={child.path}
              node={child}
              currentPath={currentPath}
              onSelectPath={onSelectPath}
              expandedPaths={expandedPaths}
              toggleExpand={toggleExpand}
              onDropOnFolder={onDropOnFolder}
              onDragOverNode={onDragOverNode}
              onDragLeaveNode={onDragLeaveNode}
              dragOverFolder={dragOverFolder}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const BibliothequeDeRessources = () => {
  // State
  const [currentPath, setCurrentPath] = useState('');
  const [items, setItems] = useState([]);
  const [folderTree, setFolderTree] = useState([]);
  const [expandedPaths, setExpandedPaths] = useState({});
  const [stats, setStats] = useState({ totalDocs: 0, totalFolders: 0, totalSizeFormatted: '0 B' });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'

  // Drag & Drop
  const [isDraggingOverCanvas, setIsDraggingOverCanvas] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const [dragCursorPos, setDragCursorPos] = useState(null);

  // Context Menu & Create Dropdown
  const [contextMenu, setContextMenu] = useState(null);
  const [createMenuOpen, setCreateMenuOpen] = useState(false);

  // Native macOS Inline Folder Creation
  const [isCreatingInlineFolder, setIsCreatingInlineFolder] = useState(false);
  const [inlineFolderName, setInlineFolderName] = useState('');
  const inlineInputRef = useRef(null);

  // Native macOS Inline Renaming
  const [editingItemPath, setEditingItemPath] = useState(null);
  const [editingItemName, setEditingItemName] = useState('');
  const [editingOriginalItem, setEditingOriginalItem] = useState(null);
  const renameInputRef = useRef(null);

  // Vibration / Shake Animation State for Empty Inputs (macOS Style)
  const [isShaking, setIsShaking] = useState(false);

  // Modals & Injections
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  // ── Focused SSH Deployment State (Single-Instance / Multi-Tenant on Same VPS) ──
  const [deployModalOpen, setDeployModalOpen] = useState(false);
  const [clientsList, setClientsList] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [sshPassword, setSshPassword] = useState('');
  const [vpsTargetMode, setVpsTargetMode] = useState('single'); // 'single' | 'all_on_vps'
  const [deployScope, setDeployScope] = useState('current'); // 'current' | 'all'
  const [folderSubScope, setFolderSubScope] = useState('all'); // 'all' | 'custom'
  const [selectedSubItemPaths, setSelectedSubItemPaths] = useState([]);
  const [deployLoading, setDeployLoading] = useState(false);
  const [deployStep, setDeployStep] = useState(1);
  const [deployStep3Info, setDeployStep3Info] = useState({ status: 'pending', label: '' });
  const [deployProgress, setDeployProgress] = useState(0);
  const [transferredBytes, setTransferredBytes] = useState(0);
  const [deployResult, setDeployResult] = useState(null);

  const [notification, setNotification] = useState(null);

  const fileInputRef = useRef(null);

  // --- Initial Load & Path Changes ---
  useEffect(() => {
    fetchFolderTree();
  }, []);

  useEffect(() => {
    fetchFolderContent(currentPath);

    if (currentPath) {
      const parts = currentPath.split('/');
      const newExpanded = { ...expandedPaths };
      let build = '';
      parts.forEach((p) => {
        build = build ? `${build}/${p}` : p;
        newExpanded[build] = true;
      });
      setExpandedPaths(newExpanded);
    }
  }, [currentPath]);

  // Update selected sub-items whenever items change
  useEffect(() => {
    setSelectedSubItemPaths(items.map((i) => i.path));
  }, [items]);

  // Close context menu and create menu on click anywhere
  useEffect(() => {
    const handleClick = () => {
      setContextMenu(null);
      setCreateMenuOpen(false);
    };
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, []);

  const showToast = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const triggerShake = () => {
    setIsShaking(false);
    setTimeout(() => {
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
    }, 20);
  };

  // --- API Calls ---
  const fetchFolderTree = async () => {
    try {
      const res = await api.get(`${API_BASE}/tree`);
      setFolderTree(res.data.tree || []);
      const initialExp = {};
      (res.data.tree || []).forEach((n) => {
        initialExp[n.path] = true;
      });
      setExpandedPaths(initialExp);
    } catch (err) {
      console.error('Error fetching tree:', err);
    }
  };

  const fetchFolderContent = async (path = '') => {
    setLoading(true);
    try {
      const res = await api.get(`${API_BASE}/content`, {
        params: { path }
      });
      const loadedItems = res.data.items || [];
      setItems(loadedItems);
      setSelectedSubItemPaths(loadedItems.map((i) => i.path));
      if (res.data.stats) {
        setStats(res.data.stats);
      }
    } catch (err) {
      console.error('Error fetching folder content:', err);
      showToast(err.response?.data?.message || 'Erreur lors du chargement des fichiers', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await api.get('/clients');
      const data = res.data;
      const list = Array.isArray(data) ? data : (data.clients || []);
      setClientsList(list);

      if (list.length > 0) {
        const firstId = list[0]._id || list[0].id;
        setSelectedClientId(firstId);
        setSshPassword('');
      }
    } catch (err) {
      console.error('Error fetching clients for deploy:', err);
    }
  };

  const toggleExpand = (nodePath) => {
    setExpandedPaths((prev) => ({
      ...prev,
      [nodePath]: !prev[nodePath]
    }));
  };

  // ── macOS Native Inline Folder Creation Handlers ──
  const startInlineFolderCreation = () => {
    setCreateMenuOpen(false);
    setInlineFolderName('');
    setIsCreatingInlineFolder(true);
    setTimeout(() => {
      inlineInputRef.current?.focus();
    }, 50);
  };

  const handleInlineFolderSubmit = async (isBlur = false) => {
    if (!isCreatingInlineFolder) return;
    const name = inlineFolderName.trim();

    // If blurred with empty text, cancel creation cleanly
    if (!name && isBlur) {
      setIsCreatingInlineFolder(false);
      setInlineFolderName('');
      return;
    }

    // If user hit Enter on empty text, trigger macOS Shake Animation on whole folder!
    if (!name) {
      triggerShake();
      inlineInputRef.current?.focus();
      return;
    }

    try {
      await api.post(`${API_BASE}/folder`, {
        currentPath,
        folderName: name
      });
      showToast(`Dossier "${name}" créé avec succès`);
      setIsCreatingInlineFolder(false);
      setInlineFolderName('');
      fetchFolderContent(currentPath);
      fetchFolderTree();
    } catch (err) {
      showToast(err.response?.data?.message || 'Erreur lors de la création du dossier', 'error');
      setIsCreatingInlineFolder(false);
    }
  };

  const cancelInlineFolderCreation = () => {
    setIsCreatingInlineFolder(false);
    setInlineFolderName('');
  };

  // ── macOS Native Inline Renaming Handlers ──
  const startInlineRename = (item) => {
    setContextMenu(null);
    setEditingItemPath(item.path);
    setEditingItemName(item.name);
    setEditingOriginalItem(item);
    setTimeout(() => {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    }, 50);
  };

  const handleInlineRenameSubmit = async (isBlur = false) => {
    if (!editingItemPath || !editingOriginalItem) return;
    const newName = editingItemName.trim();
    const oldName = editingOriginalItem.name;

    // If blurred with empty text, restore original name
    if (!newName && isBlur) {
      setEditingItemPath(null);
      setEditingOriginalItem(null);
      return;
    }

    // If user pressed Enter on empty text, trigger macOS Shake Animation on whole folder!
    if (!newName) {
      triggerShake();
      renameInputRef.current?.focus();
      return;
    }

    if (newName === oldName) {
      setEditingItemPath(null);
      setEditingOriginalItem(null);
      return;
    }

    try {
      await api.put(`${API_BASE}/rename`, {
        currentPath,
        oldName,
        newName
      });
      showToast(`"${oldName}" renommé en "${newName}"`);
      setEditingItemPath(null);
      setEditingOriginalItem(null);
      fetchFolderContent(currentPath);
      fetchFolderTree();
    } catch (err) {
      showToast(err.response?.data?.message || 'Erreur lors du renommage', 'error');
      setEditingItemPath(null);
      setEditingOriginalItem(null);
    }
  };

  const cancelInlineRename = () => {
    setEditingItemPath(null);
    setEditingOriginalItem(null);
  };

  const handleUploadFiles = async (files) => {
    if (!files || files.length === 0) return;

    const formData = new FormData();
    formData.append('currentPath', currentPath);
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    try {
      showToast('Téléversement en cours...', 'info');
      await api.post(`${API_BASE}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showToast(`${files.length} fichier(s) importé(s) avec succès`);
      fetchFolderContent(currentPath);
    } catch (err) {
      showToast(err.response?.data?.message || 'Erreur lors du téléversement', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await api.delete(`${API_BASE}/delete`, {
        data: { itemPath: deleteTarget.path }
      });
      showToast('Élément supprimé avec succès');
      setDeleteModalOpen(false);
      setDeleteTarget(null);
      fetchFolderContent(currentPath);
      fetchFolderTree();
    } catch (err) {
      showToast(err.response?.data?.message || 'Erreur lors de la suppression', 'error');
    }
  };

  const handleMove = async (sourceItem, targetFolder) => {
    if (!sourceItem || !targetFolder || targetFolder.type !== 'folder') return;
    if (sourceItem.path === targetFolder.path) return;

    try {
      await api.put(`${API_BASE}/move`, {
        sourceRelativePath: sourceItem.path,
        targetRelativeFolder: targetFolder.path
      });
      showToast(`"${sourceItem.name}" déplacé dans "${targetFolder.name}"`);
      fetchFolderContent(currentPath);
      fetchFolderTree();
    } catch (err) {
      showToast(err.response?.data?.message || 'Erreur lors du déplacement', 'error');
    }
  };

  // ── Multi-Tenant VPS Detection & Execution ──
  const selectedTenantObj = clientsList.find((c) => (c._id || c.id) === selectedClientId) || null;
  const currentVpsIp = selectedTenantObj?.vpsId || selectedTenantObj?.ip || '127.0.0.1';
  const isSelectedRemote = currentVpsIp !== 'localhost' && currentVpsIp !== '127.0.0.1' && currentVpsIp !== 'local';

  // Find all tenants hosted on the exact same VPS IP
  const tenantsOnSameVps = clientsList.filter((c) => {
    const ip = c.vpsId || c.ip || '127.0.0.1';
    return ip === currentVpsIp;
  });

  const handleSelectClient = (clientId) => {
    setSelectedClientId(clientId);
    setDeployResult(null);
    setSshPassword('');
    setVpsTargetMode('single');
  };

  const toggleSubItemSelection = (itemPath) => {
    setSelectedSubItemPaths((prev) =>
      prev.includes(itemPath) ? prev.filter((p) => p !== itemPath) : [...prev, itemPath]
    );
  };

  const handleSelectAllSubItems = () => {
    setSelectedSubItemPaths(items.map((i) => i.path));
  };

  const handleDeselectAllSubItems = () => {
    setSelectedSubItemPaths([]);
  };

  const formatBytes = (bytes, decimals = 1) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['B', 'Ko', 'Mo', 'Go', 'To'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  const getDeployPayloadStats = () => {
    if (deployScope === 'all') {
      const bytes = stats.totalSizeBytes || 4500000;
      return {
        totalBytes: bytes,
        formatted: stats.totalSizeFormatted || formatBytes(bytes),
        count: stats.totalDocs || items.length || 1
      };
    }

    const targetItems = folderSubScope === 'custom'
      ? items.filter((i) => selectedSubItemPaths.includes(i.path))
      : items;

    let bytes = 0;
    let count = 0;
    targetItems.forEach((i) => {
      if (i.type === 'file') {
        bytes += i.size || 250000;
        count++;
      } else {
        bytes += (i.itemCount || 1) * 350000;
        count += (i.itemCount || 1);
      }
    });

    if (bytes === 0) bytes = 1200000;

    return {
      totalBytes: bytes,
      formatted: formatBytes(bytes),
      count: count || targetItems.length || 1
    };
  };

  const handleExecuteDeploy = async (e) => {
    if (e) e.preventDefault();
    if (!selectedClientId) {
      showToast('Veuillez sélectionner un établissement', 'error');
      return;
    }

    if (isSelectedRemote && !sshPassword) {
      showToast('Veuillez renseigner le mot de passe SSH du VPS', 'error');
      return;
    }

    if (deployScope === 'current' && folderSubScope === 'custom' && selectedSubItemPaths.length === 0) {
      showToast('Veuillez cocher au moins un dossier ou fichier à déployer', 'error');
      return;
    }

    const payloadStats = getDeployPayloadStats();
    setDeployLoading(true);
    setDeployStep(1);
    setDeployStep3Info({ status: 'pending', label: '' });
    setDeployProgress(15);
    setTransferredBytes(Math.round(payloadStats.totalBytes * 0.15));
    setDeployResult(null);

    // Save password for all tenants on this VPS in session
    if (sshPassword) {
      tenantsOnSameVps.forEach((t) => {
        const id = t._id || t.id;
        sessionStorage.setItem(`ssh_pass_${id}`, sshPassword);
      });
    }

    // Determine target client IDs (single or all on same VPS)
    const targetClientIds = (vpsTargetMode === 'all_on_vps' && tenantsOnSameVps.length > 1)
      ? tenantsOnSameVps.map((c) => c._id || c.id)
      : [selectedClientId];

    const tenantCredentials = {};
    targetClientIds.forEach((id) => {
      const client = clientsList.find((c) => (c._id || c.id) === id);
      tenantCredentials[id] = {
        ip: client?.vpsId || client?.ip || 'localhost',
        username: client?.sshUser || 'ubuntu',
        password: sshPassword
      };
    });

    try {
      // Step 1: Connecting SSH
      await new Promise((r) => setTimeout(r, 400));
      // Step 2: Init volumes
      setDeployStep(2);
      setDeployProgress(35);
      setTransferredBytes(Math.round(payloadStats.totalBytes * 0.35));

      // Step 3: SFTP & Delta Check
      await new Promise((r) => setTimeout(r, 450));
      setDeployStep(3);
      setDeployProgress(60);

      const payload = {
        clientIds: targetClientIds,
        tenantCredentials,
        scopePath: deployScope === 'current' ? currentPath : '',
        selectedSubPaths: deployScope === 'current' && folderSubScope === 'custom' ? selectedSubItemPaths : null
      };

      const res = await api.post(`${API_BASE}/sync-tenant`, payload);

      const isUpToDate = res.data.status === 'uptodate';
      const firstResult = res.data.results?.[0];
      const uploaded = firstResult?.uploadedCount || 0;

      // ── Step 3 Explicit Resolution: clearly show if it was SKIPPED (0 modif) or TRANSFERRED ──
      const step3Badge = isUpToDate
        ? '⚡ 0 modif (Déjà à jour)'
        : uploaded > 0
        ? `✓ ${uploaded} nouveau(x) fichier(s)`
        : '✓ Arborescence prête';

      setDeployStep3Info({
        status: isUpToDate ? 'skipped' : 'uploaded',
        label: step3Badge
      });
      setDeployProgress(85);

      // Pause 700ms so the user SEES the Step 3 status clearly before moving to Step 4
      await new Promise((r) => setTimeout(r, 700));

      // ── Step 4: Finalisation permissions ──
      setDeployStep(4);
      setDeployProgress(100);
      setTransferredBytes(payloadStats.totalBytes);

      // Pause 600ms on Step 4
      await new Promise((r) => setTimeout(r, 600));

      setDeployResult({
        success: res.data.success,
        status: res.data.status || (res.data.success ? 'success' : 'failed'),
        message: res.data.message,
        results: res.data.results
      });
      showToast(
        res.data.status === 'uptodate'
          ? 'Ressources déjà à jour sur le serveur distant !'
          : 'Synchronisation SSH terminée avec succès !'
      );
    } catch (err) {
      const errMsg = err.response?.data?.message || 'Erreur de synchronisation SSH';
      setDeployResult({
        success: false,
        status: 'failed',
        message: errMsg,
        results: err.response?.data?.results || []
      });
      showToast(errMsg, 'error');
    } finally {
      setDeployLoading(false);
    }
  };

  // --- Navigation & Breadcrumbs ---
  const pathSegments = currentPath ? currentPath.split('/').filter(Boolean) : [];

  const navigateToSegment = (index) => {
    if (index === -1) {
      setCurrentPath('');
    } else {
      const newPath = pathSegments.slice(0, index + 1).join('/');
      setCurrentPath(newPath);
    }
  };

  const navigateUp = () => {
    if (!currentPath) return;
    const parentSegments = pathSegments.slice(0, -1);
    setCurrentPath(parentSegments.join('/'));
  };

  const openItem = (item) => {
    if (item.type === 'folder') {
      setCurrentPath(item.path);
    } else {
      setPreviewFile(item);
      setPreviewModalOpen(true);
    }
  };

  const getFullFileUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL.replace(/\/$/, '')}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  // --- Drag & Drop Handlers for Desktop Files ---
  const handleDragOverCanvas = (e) => {
    e.preventDefault();
    if (draggedItem) {
      setDragCursorPos({ x: e.clientX, y: e.clientY });
    }
  };

  const handleDragLeaveCanvas = (e) => {
    e.preventDefault();
    setIsDraggingOverCanvas(false);
  };

  const handleDropCanvas = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOverCanvas(false);
    setDragOverFolder(null);
    setDragCursorPos(null);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleUploadFiles(e.dataTransfer.files);
    }
  };

  // --- Drag & Drop to Move Items (Pure macOS Drag Ghost) ---
  const handleItemDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.setData('text/plain', item.path);
    setDragCursorPos({ x: e.clientX, y: e.clientY });

    // Create a pristine macOS-style Drag Ghost (just the icon & name badge, ZERO container borders or action buttons)
    const iconContainer = e.currentTarget.querySelector('.mac-icon-container') || e.currentTarget.querySelector('svg')?.parentElement;
    if (iconContainer && e.dataTransfer.setDragImage) {
      const ghost = document.createElement('div');
      ghost.style.position = 'fixed';
      ghost.style.top = '-1000px';
      ghost.style.left = '-1000px';
      ghost.style.display = 'flex';
      ghost.style.flexDirection = 'column';
      ghost.style.alignItems = 'center';
      ghost.style.justifyContent = 'center';
      ghost.style.pointerEvents = 'none';
      ghost.style.zIndex = '99999';
      ghost.style.opacity = '0.92';

      const iconClone = iconContainer.cloneNode(true);
      iconClone.style.transform = 'none';
      iconClone.style.margin = '0';
      ghost.appendChild(iconClone);

      const label = document.createElement('span');
      label.textContent = item.name;
      label.style.fontSize = '11px';
      label.style.fontWeight = '700';
      label.style.color = '#0f172a';
      label.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
      label.style.backdropFilter = 'blur(4px)';
      label.style.padding = '2px 8px';
      label.style.borderRadius = '6px';
      label.style.marginTop = '4px';
      label.style.maxWidth = '120px';
      label.style.whiteSpace = 'nowrap';
      label.style.overflow = 'hidden';
      label.style.textOverflow = 'ellipsis';
      label.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
      label.style.border = '1px solid rgba(226, 232, 240, 0.8)';
      ghost.appendChild(label);

      document.body.appendChild(ghost);
      e.dataTransfer.setDragImage(ghost, 50, 35);

      setTimeout(() => {
        if (ghost.parentNode) {
          ghost.parentNode.removeChild(ghost);
        }
      }, 0);
    }
  };

  const handleItemDragEnd = () => {
    setDraggedItem(null);
    setDragOverFolder(null);
    setDragCursorPos(null);
  };

  const handleFolderDragOver = (e, folder) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragCursorPos({ x: e.clientX, y: e.clientY });

    if (draggedItem && draggedItem.path !== folder.path) {
      if (dragOverFolder !== folder.path) {
        setDragOverFolder(folder.path);
      }
    }
  };

  const handleFolderDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverFolder(null);
    }
  };

  const handleFolderDrop = (e, targetFolder) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFolder(null);
    setDragCursorPos(null);
    if (draggedItem) {
      handleMove(draggedItem, { ...targetFolder, type: 'folder' });
      setDraggedItem(null);
    }
  };

  // --- Right-Click Context Menu ---
  const handleContextMenu = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      item
    });
  };

  // --- Filtered Items by Search ---
  const filteredItems = items.filter((item) =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase().trim())
  );

  return (
    <div
      className="flex flex-col h-full bg-slate-50 font-sans selection:bg-blue-100 selection:text-blue-900"
      onDragOver={handleDragOverCanvas}
      onDragLeave={handleDragLeaveCanvas}
      onDrop={handleDropCanvas}
    >
      {/* ── Native Apple macOS Shake Animation Keyframes ── */}
      <style>{`
        @keyframes macShake {
          0%, 100% { transform: translateX(0); }
          15% { transform: translateX(-16px); }
          30% { transform: translateX(14px); }
          45% { transform: translateX(-10px); }
          60% { transform: translateX(8px); }
          75% { transform: translateX(-4px); }
          90% { transform: translateX(2px); }
        }
        .animate-mac-shake {
          animation: macShake 0.45s cubic-bezier(0.36, 0.07, 0.19, 0.97) both !important;
        }
      `}</style>

      {/* ── macOS Floating Drag Action Pill (Follows Dragged Cursor Smoothly) ── */}
      {draggedItem && dragOverFolder && dragCursorPos && (
        <div
          style={{
            position: 'fixed',
            top: `${dragCursorPos.y + 14}px`,
            left: `${dragCursorPos.x + 18}px`,
            zIndex: 999999,
            pointerEvents: 'none'
          }}
          className="bg-slate-900/95 backdrop-blur-md text-white text-[11px] font-bold px-3 py-1.5 rounded-xl shadow-2xl border border-slate-700/80 flex items-center gap-2 animate-fadeIn"
        >
          <div className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center text-[10px] text-white shrink-0 font-black">
            ↓
          </div>
          <span>
            Déplacer vers <strong className="text-blue-300">{dragOverFolder.split('/').pop()}</strong>
          </span>
        </div>
      )}

      {/* Visual Overlay when dragging files from desktop */}
      <AnimatePresence>
        {isDraggingOverCanvas && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-blue-600/20 backdrop-blur-xs border-4 border-dashed border-blue-500 flex flex-col items-center justify-center text-blue-900 pointer-events-none"
          >
            <UploadCloud className="w-20 h-20 text-blue-600 animate-bounce" />
            <h2 className="text-2xl font-black mt-3">Déposez vos fichiers pour les importer</h2>
            <p className="text-sm font-semibold text-blue-800">
              Dans : /{currentPath || 'Racine'}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* =========================================================
           1️⃣ TOP 5 TINTED KPI CARDS (MATCHING /DASHBOARD STYLE)
      ========================================================= */}
      <div className="p-6 space-y-6 border-b border-slate-200 bg-white shrink-0">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          
          {/* Card 1: DOCUMENTS */}
          <div className="p-4 bg-blue-50/40 border border-blue-100 rounded-2xl flex flex-col justify-between shadow-xs hover:shadow-md transition-all relative overflow-hidden group">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2.5 bg-blue-100/80 text-blue-600 rounded-xl shrink-0">
                <FileText size={18} />
              </div>
              <div>
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">DOCUMENTS</span>
                <div className="text-2xl font-black tracking-tight text-slate-900">{stats.totalDocs}</div>
              </div>
            </div>
            <div className="text-[10px] font-bold text-blue-600 tracking-wider flex items-center gap-1 mt-1">
              <span>↑ Fiches & Cours Officiels</span>
            </div>
            <div className="h-7 w-full mt-2 text-blue-500/25">
              <svg className="w-full h-full" viewBox="0 0 100 25" preserveAspectRatio="none">
                <path d="M0 20 Q 25 5, 50 15 T 100 5 L 100 25 L 0 25 Z" fill="currentColor" />
                <path d="M0 20 Q 25 5, 50 15 T 100 5" fill="none" stroke="#2563eb" strokeWidth="2" />
              </svg>
            </div>
          </div>

          {/* Card 2: DOSSIERS */}
          <div className="p-4 bg-amber-50/40 border border-amber-100 rounded-2xl flex flex-col justify-between shadow-xs hover:shadow-md transition-all relative overflow-hidden group">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2.5 bg-amber-100/80 text-amber-600 rounded-xl shrink-0">
                <Folder size={18} />
              </div>
              <div>
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">DOSSIERS</span>
                <div className="text-2xl font-black tracking-tight text-slate-900">{stats.totalFolders}</div>
              </div>
            </div>
            <div className="text-[10px] font-bold text-amber-600 tracking-wider flex items-center gap-1 mt-1">
              <span>↑ Arborescence Active</span>
            </div>
            <div className="h-7 w-full mt-2 text-amber-500/25">
              <svg className="w-full h-full" viewBox="0 0 100 25" preserveAspectRatio="none">
                <path d="M0 18 Q 25 8, 50 20 T 100 4 L 100 25 L 0 25 Z" fill="currentColor" />
                <path d="M0 18 Q 25 8, 50 20 T 100 4" fill="none" stroke="#f59e0b" strokeWidth="2" />
              </svg>
            </div>
          </div>

          {/* Card 3: CYCLES */}
          <div className="p-4 bg-purple-50/40 border border-purple-100 rounded-2xl flex flex-col justify-between shadow-xs hover:shadow-md transition-all relative overflow-hidden group">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2.5 bg-purple-100/80 text-purple-600 rounded-xl shrink-0">
                <Layers size={18} />
              </div>
              <div>
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">CYCLES</span>
                <div className="text-2xl font-black tracking-tight text-slate-900">3</div>
              </div>
            </div>
            <div className="text-[10px] font-bold text-purple-600 tracking-wider flex items-center gap-1 mt-1">
              <span>✦ Primaire • Collège • Lycée</span>
            </div>
            <div className="h-7 w-full mt-2 text-purple-500/25">
              <svg className="w-full h-full" viewBox="0 0 100 25" preserveAspectRatio="none">
                <path d="M0 12 Q 25 2, 50 18 T 100 8 L 100 25 L 0 25 Z" fill="currentColor" />
                <path d="M0 12 Q 25 2, 50 18 T 100 8" fill="none" stroke="#8b5cf6" strokeWidth="2" />
              </svg>
            </div>
          </div>

          {/* Card 4: STOCKAGE */}
          <div className="p-4 bg-emerald-50/40 border border-emerald-100 rounded-2xl flex flex-col justify-between shadow-xs hover:shadow-md transition-all relative overflow-hidden group">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2.5 bg-emerald-100/80 text-emerald-600 rounded-xl shrink-0">
                <Activity size={18} />
              </div>
              <div>
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">STOCKAGE</span>
                <div className="text-2xl font-black tracking-tight text-slate-900">{stats.totalSizeFormatted}</div>
              </div>
            </div>
            <div className="text-[10px] font-bold text-emerald-600 tracking-wider flex items-center gap-1 mt-1">
              <span>✓ Volume uploads/</span>
            </div>
            <div className="h-7 w-full mt-2 text-emerald-500/25">
              <svg className="w-full h-full" viewBox="0 0 100 25" preserveAspectRatio="none">
                <path d="M0 15 Q 25 22, 50 8 T 100 2 L 100 25 L 0 25 Z" fill="currentColor" />
                <path d="M0 15 Q 25 22, 50 8 T 100 2" fill="none" stroke="#10b981" strokeWidth="2" />
              </svg>
            </div>
          </div>

          {/* Card 5: SYNCHRONISATION SSH */}
          <div className="p-4 bg-cyan-50/40 border border-cyan-100 rounded-2xl flex flex-col justify-between shadow-xs hover:shadow-md transition-all relative overflow-hidden group">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2.5 bg-cyan-100/80 text-cyan-600 rounded-xl shrink-0">
                <Server size={18} />
              </div>
              <div>
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">DÉPLOIEMENT SSH</span>
                <div className="text-2xl font-black tracking-tight text-slate-900">{clientsList.length || 0}</div>
              </div>
            </div>
            <div className="text-[10px] font-bold text-cyan-600 tracking-wider flex items-center gap-1 mt-1">
              <span>✦ Synchronisation Directe</span>
            </div>
            <div className="h-7 w-full mt-2 text-cyan-500/25">
              <svg className="w-full h-full" viewBox="0 0 100 25" preserveAspectRatio="none">
                <path d="M0 10 Q 25 22, 50 5 T 100 15 L 100 25 L 0 25 Z" fill="currentColor" />
                <path d="M0 10 Q 25 22, 50 5 T 100 15" fill="none" stroke="#06b6d4" strokeWidth="2" />
              </svg>
            </div>
          </div>

        </div>
      </div>

      {/* =========================================================
           2️⃣ MAIN WORK AREA (MACOS FINDER DUAL-PANE)
      ========================================================= */}
      <div className="p-6 space-y-3 flex-1 flex flex-col">
        
        {/* Hidden File Input */}
        <input
          type="file"
          multiple
          ref={fileInputRef}
          className="hidden"
          onChange={(e) => handleUploadFiles(e.target.files)}
        />

        {/* ── TOP ACTION TOOLBAR ── */}
        <div className="bg-white rounded-xl px-4 py-2.5 border border-slate-200/80 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-3 shrink-0">
          
          {/* Left: Quick Label */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-800">
              Explorateur de Fichiers
            </span>
          </div>

          {/* Right Action Tools */}
          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            {/* Search */}
            <div className="relative w-36 sm:w-44">
              <Search size={13} className="absolute inset-y-0 left-2.5 my-auto text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher..."
                className="w-full pl-7 pr-7 py-1 bg-slate-50 hover:bg-white focus:bg-white text-xs font-medium rounded-lg border border-slate-200 focus:outline-none focus:ring-1 focus:ring-slate-400 transition"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-2 my-auto text-slate-400 hover:text-slate-700 text-xs"
                >
                  <X size={11} />
                </button>
              )}
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1 rounded-md text-xs transition ${
                  viewMode === 'grid' ? 'bg-white text-slate-900 shadow-2xs font-bold' : 'text-slate-500 hover:text-slate-900'
                }`}
                title="Vue Grille"
              >
                <Grid size={13} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1 rounded-md text-xs transition ${
                  viewMode === 'list' ? 'bg-white text-slate-900 shadow-2xs font-bold' : 'text-slate-500 hover:text-slate-900'
                }`}
                title="Vue Liste"
              >
                <List size={13} />
              </button>
            </div>

            {/* Refresh */}
            <button
              onClick={() => {
                fetchFolderContent(currentPath);
                fetchFolderTree();
              }}
              className="p-1.5 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg text-slate-600 shadow-2xs transition"
              title="Actualiser"
            >
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            </button>

            {/* ➕ Créer Arborescence (Single Dropdown Button) */}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setCreateMenuOpen((prev) => !prev);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-bold shadow-sm transition"
              >
                <FolderPlus size={13} />
                <span>Créer Arborescence</span>
                <ChevronDown size={12} className={`transition-transform duration-150 ${createMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu (Modern Apple / Linear Design) */}
              <AnimatePresence>
                {createMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -4 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -4 }}
                    transition={{ duration: 0.12 }}
                    onClick={(e) => e.stopPropagation()}
                    className="absolute right-0 mt-2 w-60 bg-white/95 backdrop-blur-md rounded-2xl shadow-xl border border-slate-200/90 p-1.5 z-50 text-xs font-semibold text-slate-700 ring-1 ring-black/5"
                  >
                    {/* Option 1: Nouveau Dossier Inline */}
                    <button
                      onClick={startInlineFolderCreation}
                      className="w-full p-2 text-left hover:bg-slate-100/80 rounded-xl flex items-center gap-3 transition group"
                    >
                      <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 border border-amber-200/50 flex items-center justify-center shrink-0 group-hover:scale-105 group-hover:bg-amber-100 transition-all">
                        <FolderPlus size={16} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-bold text-slate-900 text-xs group-hover:text-blue-600 transition-colors">
                          Nouveau Dossier
                        </div>
                        <div className="text-[10px] text-slate-400 font-medium truncate">
                          Créer directement dans la vue
                        </div>
                      </div>
                    </button>

                    <div className="my-1 border-t border-slate-100" />

                    {/* Option 2: Fichiers */}
                    <button
                      onClick={() => {
                        setCreateMenuOpen(false);
                        fileInputRef.current?.click();
                      }}
                      className="w-full p-2 text-left hover:bg-slate-100/80 rounded-xl flex items-center gap-3 transition group"
                    >
                      <div className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 border border-blue-200/50 flex items-center justify-center shrink-0 group-hover:scale-105 group-hover:bg-blue-100 transition-all">
                        <UploadCloud size={16} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-bold text-slate-900 text-xs group-hover:text-blue-600 transition-colors">
                          Importer Fichiers
                        </div>
                        <div className="text-[10px] text-slate-400 font-medium truncate">
                          PDF, Word, Images, etc.
                        </div>
                      </div>
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* ⚡ Déployer vers une Instance (Clean Focused Modal) */}
            <button
              onClick={() => {
                fetchClients();
                setDeployModalOpen(true);
              }}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold shadow-sm transition"
            >
              <Send size={13} />
              <span>Déployer vers Tenant</span>
            </button>
          </div>

        </div>

        {/* ── DUAL-PANE CONTAINER (LEFT TREE + RIGHT MACOS CANVAS) ── */}
        <div className="bg-white rounded-xl border border-slate-200/80 shadow-xs flex-1 flex overflow-hidden min-h-[480px]">
          
          {/* 📁 LEFT NAVIGATION PANE (ARBORESCENCE) */}
          <aside className="w-60 border-r border-slate-200/80 bg-slate-50/50 p-3 flex flex-col shrink-0 overflow-y-auto">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 px-2 py-1 mb-1 flex items-center justify-between">
              <span>Arborescence</span>
            </div>

            {/* Root item */}
            <div
              onClick={() => setCurrentPath('')}
              className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg cursor-pointer transition text-xs mb-1 ${
                !currentPath
                  ? 'bg-blue-50 text-blue-700 font-bold border border-blue-200/60 shadow-2xs'
                  : 'text-slate-700 hover:bg-slate-100 font-semibold'
              }`}
            >
              <HardDrive size={14} className="text-blue-600 shrink-0" />
              <span className="truncate">Racine principale</span>
            </div>

            {/* Recursive Tree Branches */}
            <div className="space-y-0.5 flex-1">
              {folderTree.map((node) => (
                <TreeNode
                  key={node.path}
                  node={node}
                  currentPath={currentPath}
                  onSelectPath={(path) => setCurrentPath(path)}
                  expandedPaths={expandedPaths}
                  toggleExpand={toggleExpand}
                  onDropOnFolder={(target) => {
                    if (draggedItem) {
                      handleMove(draggedItem, { ...target, type: 'folder' });
                      setDraggedItem(null);
                      setDragOverFolder(null);
                      setDragCursorPos(null);
                    }
                  }}
                  onDragOverNode={(e, targetNode) => handleFolderDragOver(e, targetNode)}
                  onDragLeaveNode={handleFolderDragLeave}
                  dragOverFolder={dragOverFolder}
                />
              ))}
            </div>
          </aside>

          {/* 📂 RIGHT MAIN CONTENT PANE (PATH HEADER + INLINE MACOS FINDER CREATION & RENAMING) */}
          <main className="flex-1 flex flex-col justify-between p-5 overflow-y-auto">
            
            <div>
              {/* ── PATH HEADER DIRECTLY INSIDE THE CONTENT AREA ── */}
              <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-4 shrink-0">
                
                {/* Breadcrumbs Path */}
                <div className="flex items-center gap-1.5 min-w-0 text-xs font-semibold text-slate-500 overflow-hidden truncate">
                  {currentPath && (
                    <button
                      onClick={navigateUp}
                      className="p-1 rounded-md hover:bg-slate-100 text-slate-600 transition mr-1"
                      title="Dossier parent"
                    >
                      <ArrowLeft size={13} />
                    </button>
                  )}

                  <button
                    onClick={() => setCurrentPath('')}
                    className={`hover:text-blue-600 transition truncate ${
                      !currentPath ? 'text-blue-600 font-bold' : 'text-slate-600'
                    }`}
                  >
                    Racine
                  </button>

                  {pathSegments.map((segment, index) => {
                    const isLast = index === pathSegments.length - 1;
                    return (
                      <React.Fragment key={index}>
                        <span className="text-slate-300 mx-0.5">/</span>
                        <button
                          onClick={() => navigateToSegment(index)}
                          className={`hover:text-blue-600 transition truncate ${
                            isLast ? 'text-slate-900 font-bold' : 'text-slate-600'
                          }`}
                        >
                          {segment}
                        </button>
                      </React.Fragment>
                    );
                  })}
                </div>

                {/* Right Item Counter in Path Bar */}
                <span className="text-[10px] font-bold text-slate-400 font-mono shrink-0">
                  {filteredItems.length} élément(s)
                </span>
              </div>

              {/* ── ITEMS CANVAS ── */}
              {loading ? (
                <div className="flex flex-col items-center justify-center py-24 text-slate-400 space-y-3">
                  <RefreshCw size={22} className="animate-spin text-slate-600" />
                  <p className="text-xs font-bold">Chargement des éléments...</p>
                </div>
              ) : filteredItems.length === 0 && !isCreatingInlineFolder ? (
                <div className="flex flex-col items-center justify-center py-20 text-center space-y-3 max-w-sm mx-auto">
                  <div className="w-20 h-20 flex items-center justify-center mb-1">
                    <MacFolderIcon size={76} />
                  </div>
                  <h3 className="text-sm font-bold text-slate-800">Dossier vide</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Cliquez sur "Créer Arborescence" pour ajouter un dossier ou importez vos documents.
                  </p>
                </div>
              ) : viewMode === 'grid' ? (
                /* ── MACOS FINDER NATIVE ICON GRID (NO BORDER BOX, SUBTLE FOLDER OPENING) ── */
                <div className="flex flex-wrap gap-4 content-start items-start p-1">
                  
                  {/* Inline New Folder Spawn (Full Card Vibrates with Vivid macShake CSS, Border Only on Name Input) */}
                  {isCreatingInlineFolder && (
                    <div
                      className={`group relative w-32 p-2 rounded-2xl flex flex-col items-center text-center select-none ${
                        isShaking ? 'animate-mac-shake' : ''
                      }`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Icon vibrates together with the whole card */}
                      <div className="mac-icon-container mb-2 shrink-0 flex items-center justify-center h-16">
                        <MacFolderIcon size={68} />
                      </div>

                      {/* Border ONLY on input box */}
                      <div className="w-full px-1 min-h-[34px] flex items-center justify-center">
                        <input
                          ref={inlineInputRef}
                          type="text"
                          autoFocus
                          placeholder="Nom du dossier..."
                          value={inlineFolderName}
                          onChange={(e) => setInlineFolderName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleInlineFolderSubmit(false);
                            if (e.key === 'Escape') cancelInlineFolderCreation();
                          }}
                          onBlur={() => handleInlineFolderSubmit(true)}
                          className={`w-full text-xs font-bold text-center text-slate-900 bg-white border ${
                            isShaking
                              ? 'border-rose-500 ring-2 ring-rose-400/50 shadow-sm'
                              : 'border-blue-500 ring-2 ring-blue-400/50 shadow-xs'
                          } rounded-md px-1.5 py-1 focus:outline-none transition-all`}
                        />
                      </div>

                      <span className={`text-[10px] font-bold mt-1 ${isShaking ? 'text-rose-600' : 'text-blue-600'}`}>
                        {isShaking ? 'Nom obligatoire !' : 'Entrée ↵'}
                      </span>
                    </div>
                  )}

                  {filteredItems.map((item) => {
                    const isFolder = item.type === 'folder';
                    const isOver = dragOverFolder === item.path;
                    const isRenaming = editingItemPath === item.path;

                    return (
                      <div
                        key={item.path}
                        title={item.name}
                        draggable={!isRenaming}
                        onDragStart={(e) => handleItemDragStart(e, item)}
                        onDragEnd={handleItemDragEnd}
                        onDragOver={(e) => isFolder && handleFolderDragOver(e, item)}
                        onDragLeave={(e) => isFolder && handleFolderDragLeave(e)}
                        onDrop={(e) => isFolder && handleFolderDrop(e, item)}
                        onDoubleClick={() => !isRenaming && openItem(item)}
                        onClick={() => !isRenaming && isFolder && openItem(item)}
                        onContextMenu={(e) => handleContextMenu(e, item)}
                        className={`group relative w-32 p-2 rounded-2xl transition-all duration-150 flex flex-col items-center text-center cursor-pointer select-none ${
                          isRenaming && isShaking
                            ? 'animate-mac-shake'
                            : 'hover:bg-slate-100/70 active:bg-blue-50/70'
                        }`}
                      >
                        {/* macOS High-Fidelity Icon Container (Subtle Spring-Loaded Open State on Drag-Over, NO BORDER BOX) */}
                        <div className="mac-icon-container mb-2 shrink-0 flex items-center justify-center h-16 pointer-events-none">
                          {isFolder ? (
                            <MacFolderIcon size={68} isOpen={isOver} />
                          ) : (
                            <MacDocIcon ext={item.ext || 'pdf'} size={50} />
                          )}
                        </div>

                        {/* Inline macOS Renaming or Label (Highlight on Drag-Over Target Label) */}
                        <div className="w-full px-1 min-h-[34px] flex items-center justify-center pointer-events-none">
                          {isRenaming ? (
                            <input
                              ref={renameInputRef}
                              type="text"
                              autoFocus
                              placeholder="Nom..."
                              value={editingItemName}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => setEditingItemName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleInlineRenameSubmit(false);
                                if (e.key === 'Escape') cancelInlineRename();
                              }}
                              onBlur={() => handleInlineRenameSubmit(true)}
                              className={`w-full text-xs font-bold text-center text-slate-900 bg-white border pointer-events-auto ${
                                isShaking
                                  ? 'border-rose-500 ring-2 ring-rose-400/50 shadow-sm'
                                  : 'border-blue-500 ring-2 ring-blue-400/50 shadow-xs'
                              } rounded-md px-1.5 py-1 focus:outline-none transition-all`}
                            />
                          ) : (
                            <span
                              className={`text-xs font-semibold line-clamp-2 leading-tight text-center break-normal transition-all ${
                                isOver
                                  ? 'bg-blue-600 text-white px-2 py-0.5 rounded-md shadow-xs font-bold'
                                  : 'text-slate-800 group-hover:text-blue-600'
                              }`}
                              style={{ overflowWrap: 'normal', wordBreak: 'normal' }}
                            >
                              {item.name}
                            </span>
                          )}
                        </div>

                        {/* Subtitle / Meta */}
                        <span className={`text-[10px] font-medium mt-1 pointer-events-none ${isRenaming && isShaking ? 'text-rose-600 font-bold' : 'text-slate-400'}`}>
                          {isRenaming
                            ? isShaking
                              ? 'Nom obligatoire !'
                              : 'Entrée ↵'
                            : isFolder
                            ? `${item.itemCount || 0} élém.`
                            : item.sizeFormatted}
                        </span>

                        {/* Quick Action Overlay on Hover */}
                        {!isRenaming && !draggedItem && (
                          <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 bg-white/95 backdrop-blur-xs p-0.5 rounded-md shadow-xs border border-slate-200">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                startInlineRename(item);
                              }}
                              className="p-1 text-slate-600 hover:text-blue-600 transition text-[10px]"
                              title="Renommer (macOS)"
                            >
                              <Edit2 size={10} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteTarget(item);
                                setDeleteModalOpen(true);
                              }}
                              className="p-1 text-slate-600 hover:text-rose-600 transition text-[10px]"
                              title="Supprimer"
                            >
                              <Trash2 size={10} />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* ── LIST VIEW ── */
                <div className="space-y-1">
                  <div className="grid grid-cols-12 px-3 py-1 text-slate-400 font-black uppercase tracking-widest text-[9px]">
                    <span className="col-span-6">Nom de l'élément</span>
                    <span className="col-span-2 text-right">Taille / Éléments</span>
                    <span className="col-span-3 text-right">Dernière modification</span>
                    <span className="col-span-1 text-right">Actions</span>
                  </div>

                  {/* Inline New Folder Row (Whole Row Shakes with macShake, Border Only on Input) */}
                  {isCreatingInlineFolder && (
                    <div
                      className={`grid grid-cols-12 items-center p-2 rounded-lg bg-slate-50/70 select-none text-xs font-medium ${
                        isShaking ? 'animate-mac-shake' : ''
                      }`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="col-span-6 flex items-center gap-2.5 min-w-0 pr-2">
                        <div className="mac-icon-container w-6 h-6 shrink-0 flex items-center justify-center">
                          <Folder size={18} className="text-sky-500 fill-sky-400/20" />
                        </div>
                        <input
                          ref={inlineInputRef}
                          type="text"
                          autoFocus
                          placeholder="Nom du dossier..."
                          value={inlineFolderName}
                          onChange={(e) => setInlineFolderName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleInlineFolderSubmit(false);
                            if (e.key === 'Escape') cancelInlineFolderCreation();
                          }}
                          onBlur={() => handleInlineFolderSubmit(true)}
                          className={`text-xs font-bold text-slate-900 bg-white border ${
                            isShaking
                              ? 'border-rose-500 ring-2 ring-rose-400/50'
                              : 'border-blue-500 ring-2 ring-blue-400/50'
                          } rounded px-2 py-1 shadow-xs focus:outline-none max-w-xs transition-all`}
                        />
                      </div>
                      <div className="col-span-2 text-right text-slate-400 font-mono text-[11px]">0 élém.</div>
                      <div className="col-span-3 text-right text-slate-400 text-[10px]">Aujourd'hui</div>
                      <div className={`col-span-1 text-right text-[10px] font-bold ${isShaking ? 'text-rose-600' : 'text-blue-600'}`}>
                        {isShaking ? 'Erreur !' : 'Entrée ↵'}
                      </div>
                    </div>
                  )}

                  {filteredItems.map((item) => {
                    const isFolder = item.type === 'folder';
                    const isOver = dragOverFolder === item.path;
                    const isRenaming = editingItemPath === item.path;

                    return (
                      <div
                        key={item.path}
                        draggable={!isRenaming}
                        onDragStart={(e) => handleItemDragStart(e, item)}
                        onDragEnd={handleItemDragEnd}
                        onDragOver={(e) => isFolder && handleFolderDragOver(e, item)}
                        onDragLeave={(e) => isFolder && handleFolderDragLeave(e)}
                        onDrop={(e) => isFolder && handleFolderDrop(e, item)}
                        onDoubleClick={() => !isRenaming && openItem(item)}
                        onClick={() => !isRenaming && isFolder && openItem(item)}
                        onContextMenu={(e) => handleContextMenu(e, item)}
                        className={`grid grid-cols-12 items-center p-2 rounded-lg transition-colors border select-none text-xs font-medium cursor-pointer relative ${
                          isRenaming && isShaking
                            ? 'animate-mac-shake'
                            : isOver
                            ? 'bg-blue-50/90 text-blue-900'
                            : 'hover:bg-slate-50 border-transparent hover:border-slate-100'
                        }`}
                      >
                        <div className="col-span-6 flex items-center gap-2.5 min-w-0 pr-2 pointer-events-none">
                          <div className="mac-icon-container w-6 h-6 shrink-0 flex items-center justify-center">
                            {isFolder ? (
                              isOver ? (
                                <FolderOpen size={18} className="text-sky-600" />
                              ) : (
                                <Folder size={18} className="text-sky-500 fill-sky-400/20" />
                              )
                            ) : (
                              <FileText size={18} className="text-rose-500" />
                            )}
                          </div>
                          {isRenaming ? (
                            <input
                              ref={renameInputRef}
                              type="text"
                              autoFocus
                              placeholder="Nom..."
                              value={editingItemName}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => setEditingItemName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleInlineRenameSubmit(false);
                                if (e.key === 'Escape') cancelInlineRename();
                              }}
                              onBlur={() => handleInlineRenameSubmit(true)}
                              className={`text-xs font-bold text-slate-900 bg-white border pointer-events-auto ${
                                isShaking
                                  ? 'border-rose-500 ring-2 ring-rose-400/50'
                                  : 'border-blue-500 ring-2 ring-blue-400/50'
                              } rounded px-2 py-1 shadow-xs focus:outline-none max-w-xs transition-all`}
                            />
                          ) : (
                            <span className={`font-semibold truncate ${isOver ? 'text-blue-800 font-bold' : 'text-slate-800 hover:text-blue-600'}`}>
                              {item.name}
                            </span>
                          )}
                        </div>

                        <div className="col-span-2 text-right text-slate-500 font-mono text-[11px] pointer-events-none">
                          {isFolder ? `${item.itemCount || 0} élém.` : item.sizeFormatted}
                        </div>

                        <div className="col-span-3 text-right text-slate-400 text-[10px] pointer-events-none">
                          {item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : '-'}
                        </div>

                        <div className="col-span-1 text-right flex items-center justify-end gap-1">
                          {isOver ? (
                            <span className="text-[10px] font-bold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded animate-fadeIn">
                              → Déposer
                            </span>
                          ) : (
                            <>
                              {!isFolder && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPreviewFile(item);
                                    setPreviewModalOpen(true);
                                  }}
                                  className="p-1 text-slate-400 hover:text-blue-600 transition"
                                  title="Consulter"
                                >
                                  <Eye size={13} />
                                </button>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startInlineRename(item);
                                }}
                                className="p-1 text-slate-400 hover:text-amber-600 transition"
                                title="Renommer"
                              >
                                <Edit2 size={13} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteTarget(item);
                                  setDeleteModalOpen(true);
                                }}
                                className="p-1 text-slate-400 hover:text-rose-600 transition"
                                title="Supprimer"
                              >
                                <Trash2 size={13} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── BOTTOM STATUS BAR ── */}
            <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-6">
              <span>{filteredItems.length} élément(s) dans ce dossier</span>
              <span>Total : {stats.totalDocs} documents ({stats.totalSizeFormatted})</span>
            </div>

          </main>

        </div>

      </div>

      {/* =========================================================
           CUSTOM RIGHT-CLICK CONTEXT MENU
      ========================================================= */}
      {contextMenu && (
        <div
          style={{ top: `${contextMenu.y}px`, left: `${contextMenu.x}px` }}
          className="fixed z-50 bg-white rounded-xl shadow-xl border border-slate-200 py-1.5 w-48 text-xs font-semibold text-slate-700 animate-scaleUp"
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenu.item.type === 'folder' ? (
            <button
              onClick={() => {
                openItem(contextMenu.item);
                setContextMenu(null);
              }}
              className="w-full px-3 py-1.5 text-left hover:bg-slate-50 flex items-center gap-2"
            >
              <Folder size={14} className="text-sky-500" />
              <span>Ouvrir le dossier</span>
            </button>
          ) : (
            <button
              onClick={() => {
                setPreviewFile(contextMenu.item);
                setPreviewModalOpen(true);
                setContextMenu(null);
              }}
              className="w-full px-3 py-1.5 text-left hover:bg-slate-50 flex items-center gap-2"
            >
              <Eye size={14} className="text-blue-600" />
              <span>Consulter l'aperçu</span>
            </button>
          )}

          {/* Inline Renaming from Context Menu */}
          <button
            onClick={() => startInlineRename(contextMenu.item)}
            className="w-full px-3 py-1.5 text-left hover:bg-slate-50 flex items-center gap-2"
          >
            <Edit2 size={14} className="text-slate-500" />
            <span>Renommer</span>
          </button>

          {!contextMenu.item.type === 'folder' && (
            <a
              href={getFullFileUrl(contextMenu.item.url)}
              download={contextMenu.item.name}
              className="w-full px-3 py-1.5 text-left hover:bg-slate-50 flex items-center gap-2"
              onClick={() => setContextMenu(null)}
            >
              <Download size={14} className="text-emerald-600" />
              <span>Télécharger</span>
            </a>
          )}

          <div className="my-1 border-t border-slate-100"></div>

          <button
            onClick={() => {
              setDeleteTarget(contextMenu.item);
              setDeleteModalOpen(true);
              setContextMenu(null);
            }}
            className="w-full px-3 py-1.5 text-left hover:bg-rose-50 text-rose-600 flex items-center gap-2"
          >
            <Trash2 size={14} />
            <span>Supprimer</span>
          </button>
        </div>
      )}

      {/* =========================================================
           MODAL: CONFIRMATION SUPPRESSION
      ========================================================= */}
      {deleteModalOpen && deleteTarget && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl border border-slate-200 space-y-4">
            <div className="flex items-center gap-3 text-rose-600">
              <div className="w-8 h-8 rounded-lg bg-rose-50 border border-rose-200 flex items-center justify-center shrink-0">
                <AlertCircle size={16} />
              </div>
              <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider">
                Confirmer la suppression
              </h3>
            </div>

            <p className="text-xs text-slate-600 leading-relaxed">
              Êtes-vous sûr de vouloir supprimer définitivement <strong className="text-slate-900">{deleteTarget.name}</strong> ?
              {deleteTarget.type === 'folder' && ' Tous les sous-dossiers et fichiers contenus seront également supprimés.'}
            </p>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setDeleteModalOpen(false)}
                className="px-3.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="px-3.5 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold transition shadow-xs"
              >
                Supprimer définitivement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================
           MODAL: LECTEUR PDF INTÉGRÉ
      ========================================================= */}
      {previewModalOpen && previewFile && (
        <div
          className="fixed inset-0 z-50 bg-slate-950/75 backdrop-blur-xs flex items-center justify-center p-4"
          onClick={() => setPreviewModalOpen(false)}
        >
          <div
            className="relative w-full max-w-5xl h-[90vh] bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="h-12 px-4 bg-slate-900 text-white flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="text-rose-400" size={15} />
                <h3 className="text-xs font-bold truncate max-w-md">{previewFile.name}</h3>
              </div>

              <div className="flex items-center gap-2">
                <a
                  href={getFullFileUrl(previewFile.url)}
                  download={previewFile.name}
                  className="px-3 py-1 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold transition flex items-center gap-1.5"
                >
                  <Download size={12} />
                  <span>Télécharger</span>
                </a>
                <button
                  onClick={() => setPreviewModalOpen(false)}
                  className="w-7 h-7 rounded-md text-slate-400 hover:text-white hover:bg-white/10 flex items-center justify-center transition"
                >
                  <X size={15} />
                </button>
              </div>
            </div>

            {/* Viewer */}
            <div className="flex-1 bg-slate-100">
              <iframe
                src={getFullFileUrl(previewFile.url)}
                className="w-full h-full border-0"
                title="Aperçu du document"
              />
            </div>
          </div>
        </div>
      )}

      {/* =========================================================
           3️⃣ PRO FOCUSED SSH DEPLOYMENT SIDE DRAWER (RIGHT SLIDE-OVER SHEET)
      ========================================================= */}
      <AnimatePresence>
        {deployModalOpen && (
          <div className="fixed inset-0 z-50 overflow-hidden">
            {/* Backdrop with smooth fade */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => !deployLoading && setDeployModalOpen(false)}
              className="absolute inset-0 bg-slate-950/40 backdrop-blur-xs"
            />

            {/* Slide-over Drawer Panel from Right */}
            <div className="fixed inset-y-0 right-0 max-w-full flex pl-6 sm:pl-10">
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                className="w-screen max-w-lg bg-white shadow-2xl border-l border-slate-200 flex flex-col h-full overflow-hidden relative z-10"
              >
                {/* Drawer Header */}
                <div className="p-5 pb-4 border-b border-slate-100 relative shrink-0 bg-slate-50/60">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-indigo-50 text-indigo-600 ring-6 ring-indigo-50/50 shadow-2xs shrink-0">
                        <ShieldCheck size={24} />
                      </div>
                      <div>
                        <h2 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                          Déploiement & Sync SSH
                        </h2>
                        <p className="text-slate-500 text-[11px] mt-0.5 leading-tight">
                          Synchronisation directe vers le VPS de l'établissement.
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={() => !deployLoading && setDeployModalOpen(false)}
                      disabled={deployLoading}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 transition disabled:opacity-30 shrink-0"
                    >
                      <X size={18} />
                    </button>
                  </div>
                </div>

                {/* Drawer Body: Loading Pipeline OR Configuration Form */}
                {deployLoading ? (
                  <div className="p-6 space-y-4 flex flex-col justify-center bg-white text-slate-800 flex-1 overflow-y-auto select-none">
                    
                    {/* Top Header Card (Light Shield & Pulse) */}
                    <div className="flex items-center gap-3 p-3 bg-indigo-50/70 border border-indigo-100 rounded-2xl">
                      <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center shadow-md shadow-indigo-500/20 shrink-0">
                        <Server size={20} className="animate-pulse" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-black uppercase tracking-widest text-indigo-600">
                            Synchronisation SSH en Direct
                          </span>
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-black bg-emerald-100 text-emerald-700 border border-emerald-200">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                            <span>En cours</span>
                          </span>
                        </div>
                        <div className="text-xs font-bold text-slate-900 truncate">
                          {selectedTenantObj?.name} <span className="font-mono text-slate-400 font-normal">({currentVpsIp})</span>
                        </div>
                      </div>
                    </div>

                    {/* Live Progress Card (Volume Transféré vs Total + Barre de Progression Dégradée) */}
                    <div className="p-4 bg-slate-50/80 rounded-2xl border border-slate-200/80 space-y-2.5 shadow-2xs">
                      <div className="flex items-center justify-between text-xs font-bold">
                        <span className="text-slate-700 flex items-center gap-1.5">
                          <span>Progression du Déploiement</span>
                          {deployStep === 3 && (
                            <span className="text-[10px] text-indigo-600 font-normal">
                              (Vérification différentielle)
                            </span>
                          )}
                        </span>
                        <div className="font-mono text-indigo-600 flex items-center gap-1.5">
                          <span>{formatBytes(transferredBytes)}</span>
                          <span className="text-slate-300">/</span>
                          <span className="text-slate-500">{getDeployPayloadStats().formatted}</span>
                        </div>
                      </div>

                      {/* Main Smooth Gradient Progress Bar */}
                      <div className="w-full bg-slate-200/70 h-3 rounded-full overflow-hidden p-0.5 border border-slate-300/40">
                        <div
                          className="h-full bg-gradient-to-r from-indigo-600 via-blue-500 to-indigo-500 rounded-full transition-all duration-200 shadow-xs flex items-center justify-end pr-1"
                          style={{ width: `${Math.max(deployProgress, 8)}%` }}
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-white animate-ping" />
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-[10px] text-slate-500 font-semibold pt-0.5">
                        <span className="text-indigo-600 font-bold">{deployProgress}% complété</span>
                        <span className="flex items-center gap-1 text-slate-500">
                          <Zap size={11} className="text-amber-500" />
                          <span>Smart Delta Sync actif (saute les doublons)</span>
                        </span>
                      </div>
                    </div>

                    {/* The 4 Pipeline Steps (Light Clean Cards) */}
                    <div className="p-3.5 bg-white rounded-2xl border border-slate-200 space-y-2 text-xs font-medium shadow-2xs">
                      {/* Step 1 */}
                      <div className="flex items-center justify-between p-1.5 rounded-lg">
                        <div className="flex items-center gap-2.5">
                          {deployStep >= 2 ? (
                            <div className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                              <CheckCircle2 size={13} />
                            </div>
                          ) : (
                            <div className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0">
                              <Loader2 size={13} className="animate-spin" />
                            </div>
                          )}
                          <span className={deployStep >= 2 ? 'text-slate-700 font-semibold' : 'text-indigo-700 font-bold'}>
                            1. Tunnel SSH sécurisé (Port 22)
                          </span>
                        </div>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${
                          deployStep >= 2 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-indigo-50 text-indigo-700 animate-pulse'
                        }`}>
                          {deployStep >= 2 ? '✓ Connecté' : 'Connexion...'}
                        </span>
                      </div>

                      {/* Step 2 */}
                      <div className="flex items-center justify-between p-1.5 rounded-lg">
                        <div className="flex items-center gap-2.5">
                          {deployStep >= 3 ? (
                            <div className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                              <CheckCircle2 size={13} />
                            </div>
                          ) : deployStep === 2 ? (
                            <div className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0">
                              <Loader2 size={13} className="animate-spin" />
                            </div>
                          ) : (
                            <div className="w-5 h-5 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center shrink-0 text-[10px]">
                              2
                            </div>
                          )}
                          <span className={deployStep >= 3 ? 'text-slate-700 font-semibold' : deployStep === 2 ? 'text-indigo-700 font-bold' : 'text-slate-400'}>
                            2. Initialisation des volumes uploads
                          </span>
                        </div>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${
                          deployStep >= 3
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : deployStep === 2
                            ? 'bg-indigo-50 text-indigo-700 animate-pulse'
                            : 'text-slate-400 bg-slate-50'
                        }`}>
                          {deployStep >= 3 ? '✓ Prêt' : deployStep === 2 ? 'En cours...' : 'En attente'}
                        </span>
                      </div>

                      {/* Step 3: Smart Delta Sync & Transfert SFTP */}
                      <div className="flex items-center justify-between p-1.5 rounded-lg">
                        <div className="flex items-center gap-2.5">
                          {deployStep >= 4 || deployStep3Info.status !== 'pending' ? (
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                              deployStep3Info.status === 'skipped'
                                ? 'bg-sky-100 text-sky-600'
                                : 'bg-emerald-100 text-emerald-600'
                            }`}>
                              <CheckCircle2 size={13} />
                            </div>
                          ) : deployStep === 3 ? (
                            <div className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0">
                              <Loader2 size={13} className="animate-spin" />
                            </div>
                          ) : (
                            <div className="w-5 h-5 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center shrink-0 text-[10px]">
                              3
                            </div>
                          )}
                          <div>
                            <span className={deployStep >= 3 ? 'text-slate-700 font-semibold' : 'text-slate-400'}>
                              3. Transfert SFTP & Détection Différentielle
                            </span>
                          </div>
                        </div>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${
                          deployStep3Info.status === 'skipped'
                            ? 'bg-sky-50 text-sky-700 border border-sky-200'
                            : deployStep >= 4 || deployStep3Info.status === 'uploaded'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : deployStep === 3
                            ? 'bg-indigo-50 text-indigo-700 animate-pulse'
                            : 'text-slate-400 bg-slate-50'
                        }`}>
                          {deployStep3Info.label || (deployStep === 3 ? 'Vérif & Transfert...' : 'En attente')}
                        </span>
                      </div>

                      {/* Step 4 */}
                      <div className="flex items-center justify-between p-1.5 rounded-lg">
                        <div className="flex items-center gap-2.5">
                          {deployStep >= 4 ? (
                            <div className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                              <CheckCircle2 size={13} />
                            </div>
                          ) : (
                            <div className="w-5 h-5 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center shrink-0 text-[10px]">
                              4
                            </div>
                          )}
                          <span className={deployStep >= 4 ? 'text-slate-700 font-semibold' : 'text-slate-400'}>
                            4. Permissions Linux (ubuntu:ubuntu)
                          </span>
                        </div>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${
                          deployStep >= 4
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : 'text-slate-400 bg-slate-50'
                        }`}>
                          {deployStep >= 4 ? '✓ Finalisé' : 'En attente'}
                        </span>
                      </div>
                    </div>

                    {/* Footer Info */}
                    <div className="text-[10px] text-slate-400 font-mono text-center flex items-center justify-center gap-1.5 pt-1">
                      <ShieldCheck size={12} className="text-emerald-500" />
                      <span>Destination : /opt/xora/uploads/{selectedTenantObj?.DbName}/</span>
                    </div>

                  </div>
                ) : (
                  <form onSubmit={handleExecuteDeploy} className="flex flex-col flex-1 overflow-hidden">
                    <div className="p-5 space-y-4 text-xs font-semibold text-slate-700 bg-slate-50/40 overflow-y-auto flex-1">
                      {/* School Selection */}
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
                          Établissement Cible :
                        </label>
                        <select
                          value={selectedClientId}
                          onChange={(e) => handleSelectClient(e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition shadow-2xs font-semibold text-slate-800"
                        >
                          <option value="">-- Choisir une école / tenant --</option>
                          {clientsList.map((c) => {
                            const isRemote = (c.vpsId && c.vpsId !== 'localhost') || (c.ip && c.ip !== 'localhost' && c.ip !== '127.0.0.1');
                            return (
                              <option key={c._id || c.id} value={c._id || c.id}>
                                {c.name || c.company_name} ({isRemote ? `VPS: ${c.vpsId || c.ip}` : 'Local Docker'})
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      {/* Same VPS Multi-Tenant Smart Card */}
                      {tenantsOnSameVps.length > 1 && (
                        <div className="p-3.5 bg-amber-50/80 border border-amber-200 rounded-xl space-y-2 text-xs shadow-2xs animate-fadeIn">
                          <div className="flex items-center gap-1.5 font-bold text-amber-900">
                            <Activity size={14} className="text-amber-600 shrink-0" />
                            <span>Plusieurs écoles hébergées sur ce même VPS ({currentVpsIp})</span>
                          </div>
                          
                          <p className="text-[11px] text-amber-800 leading-relaxed font-normal">
                            Ce serveur héberge <strong>{tenantsOnSameVps.length} écoles</strong>. Vous pouvez synchroniser uniquement l'école choisie ou propager les documents à l'ensemble du VPS en un seul clic.
                          </p>

                          <div className="grid grid-cols-2 gap-2 pt-1">
                            <button
                              type="button"
                              onClick={() => setVpsTargetMode('single')}
                              className={`p-2 rounded-lg border text-left transition flex flex-col justify-between ${
                                vpsTargetMode === 'single'
                                  ? 'bg-amber-100/90 border-amber-500 text-amber-950 font-bold ring-1 ring-amber-400'
                                  : 'bg-white/80 border-amber-200 text-amber-800 hover:bg-amber-100/50'
                              }`}
                            >
                              <span className="text-[11px] font-bold">● Uniquement {selectedTenantObj?.name || 'cette école'}</span>
                              <span className="text-[9px] text-amber-600 font-mono">/opt/xora/uploads/{selectedTenantObj?.DbName}</span>
                            </button>

                            <button
                              type="button"
                              onClick={() => setVpsTargetMode('all_on_vps')}
                              className={`p-2 rounded-lg border text-left transition flex flex-col justify-between ${
                                vpsTargetMode === 'all_on_vps'
                                  ? 'bg-amber-100/90 border-amber-500 text-amber-950 font-bold ring-1 ring-amber-400'
                                  : 'bg-white/80 border-amber-200 text-amber-800 hover:bg-amber-100/50'
                              }`}
                            >
                              <span className="text-[11px] font-bold">● Toutes les {tenantsOnSameVps.length} écoles du VPS</span>
                              <span className="text-[9px] text-amber-600 font-mono">Sync groupée multi-écoles</span>
                            </button>
                          </div>

                          {vpsTargetMode === 'all_on_vps' && (
                            <div className="flex flex-wrap gap-1 pt-1.5 border-t border-amber-200/80">
                              {tenantsOnSameVps.map((t) => (
                                <span
                                  key={t._id || t.id}
                                  className="bg-white border border-amber-300 text-amber-900 px-2 py-0.5 rounded text-[10px] font-bold flex items-center gap-1 shadow-2xs"
                                >
                                  <Building2 size={10} className="text-amber-600" />
                                  <span>{t.name || t.company_name}</span>
                                  <span className="font-mono text-[9px] text-slate-400">({t.DbName})</span>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Scope Selection */}
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
                          Périmètre de Déploiement :
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => setDeployScope('current')}
                            className={`p-2.5 rounded-xl border text-left transition flex items-center gap-2 ${
                              deployScope === 'current'
                                ? 'bg-blue-50 border-blue-500 text-blue-900 font-bold ring-1 ring-blue-400/30'
                                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                            }`}
                          >
                            <Folder size={15} className="text-blue-600 shrink-0" />
                            <div className="truncate min-w-0">
                              <div className="truncate">Dossier Actif</div>
                              <div className="text-[10px] font-mono text-slate-400 truncate">/{currentPath || 'Racine'}</div>
                            </div>
                          </button>

                          <button
                            type="button"
                            onClick={() => setDeployScope('all')}
                            className={`p-2.5 rounded-xl border text-left transition flex items-center gap-2 ${
                              deployScope === 'all'
                                ? 'bg-emerald-50 border-emerald-500 text-emerald-900 font-bold ring-1 ring-emerald-400/30'
                                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                            }`}
                          >
                            <Layers size={15} className="text-emerald-600 shrink-0" />
                            <div className="truncate min-w-0">
                              <div className="truncate">Bibliothèque Complète</div>
                              <div className="text-[10px] text-slate-400">{stats.totalDocs} fichiers</div>
                            </div>
                          </button>
                        </div>
                      </div>

                      {/* SUB-SCOPE SELECTOR WHEN IN ACTIVE FOLDER */}
                      {deployScope === 'current' && (
                        <div className="p-3 bg-white rounded-xl border border-blue-200/90 shadow-2xs space-y-2.5 animate-fadeIn">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5">
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-600 flex items-center gap-1">
                              <Filter size={11} className="text-blue-600" />
                              <span>Contenu à injecter :</span>
                            </span>

                            {/* Toggle: Tout le dossier vs Sélection manuelle */}
                            <div className="flex items-center gap-1 bg-slate-100 p-0.5 rounded-lg border border-slate-200 text-[11px]">
                              <button
                                type="button"
                                onClick={() => setFolderSubScope('all')}
                                className={`px-2 py-0.5 rounded-md font-bold transition ${
                                  folderSubScope === 'all'
                                    ? 'bg-white text-blue-700 shadow-2xs border border-slate-200'
                                    : 'text-slate-500 hover:text-slate-800'
                                }`}
                              >
                                Tout le dossier ({items.length})
                              </button>
                              <button
                                type="button"
                                onClick={() => setFolderSubScope('custom')}
                                className={`px-2 py-0.5 rounded-md font-bold transition ${
                                  folderSubScope === 'custom'
                                    ? 'bg-white text-blue-700 shadow-2xs border border-slate-200'
                                    : 'text-slate-500 hover:text-slate-800'
                                }`}
                              >
                                Cocher des éléments
                              </button>
                            </div>
                          </div>

                          {/* Interactive Checklist of Sub-folders and files */}
                          {folderSubScope === 'custom' && (
                            <div className="space-y-1.5 pt-1 border-t border-slate-100">
                              <div className="flex items-center justify-between text-[10px] text-slate-500 pb-0.5">
                                <span>{selectedSubItemPaths.length} élément(s) sélectionné(s)</span>
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={handleSelectAllSubItems}
                                    className="text-blue-600 font-bold hover:underline"
                                  >
                                    Tout cocher
                                  </button>
                                  <span>•</span>
                                  <button
                                    type="button"
                                    onClick={handleDeselectAllSubItems}
                                    className="text-slate-500 font-bold hover:underline"
                                  >
                                    Décocher tout
                                  </button>
                                </div>
                              </div>

                              <div className="max-h-40 overflow-y-auto space-y-1 pr-1 border border-slate-100 rounded-lg p-1.5 bg-slate-50/50">
                                {items.length === 0 ? (
                                  <div className="text-center py-3 text-slate-400 text-[11px]">
                                    Ce dossier est vide.
                                  </div>
                                ) : (
                                  items.map((item) => {
                                    const isChecked = selectedSubItemPaths.includes(item.path);
                                    return (
                                      <div
                                        key={item.path}
                                        onClick={() => toggleSubItemSelection(item.path)}
                                        className={`flex items-center justify-between p-1.5 rounded-md cursor-pointer transition text-[11px] ${
                                          isChecked
                                            ? 'bg-blue-50/80 border border-blue-200 text-blue-900 font-bold'
                                            : 'bg-white border border-slate-200/80 text-slate-700 hover:bg-slate-100/70'
                                        }`}
                                      >
                                        <div className="flex items-center gap-2 min-w-0 truncate">
                                          <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => {}}
                                            className="rounded text-blue-600 focus:ring-blue-500 pointer-events-none"
                                          />
                                          {item.type === 'folder' ? (
                                            <Folder size={13} className="text-amber-500 shrink-0" />
                                          ) : (
                                            <FileText size={13} className="text-blue-500 shrink-0" />
                                          )}
                                          <span className="truncate">{item.name}</span>
                                        </div>

                                        <span className="text-[10px] text-slate-400 font-mono shrink-0 ml-2">
                                          {item.type === 'folder'
                                            ? `${item.itemCount || 0} élém.`
                                            : item.sizeFormatted || ''}
                                        </span>
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* SSH Credentials Card */}
                      <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1">
                            <Lock size={11} className="text-indigo-600" />
                            <span>Authentification VPS</span>
                          </span>
                          <span className="text-[10px] font-mono text-slate-400">Port: 22 (SSH/SFTP)</span>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-[10px] text-slate-600 mb-1 font-bold">
                              VPS IP / Host
                            </label>
                            <input
                              type="text"
                              name="username"
                              id="vps_ip_display"
                              autoComplete="username"
                              readOnly
                              value={currentVpsIp}
                              className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-600 font-mono text-xs cursor-not-allowed"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] text-slate-600 mb-1 font-bold">
                              SSH User
                            </label>
                            <input
                              type="text"
                              disabled
                              value={selectedTenantObj?.sshUser || 'ubuntu'}
                              className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-600 font-mono text-xs cursor-not-allowed"
                            />
                          </div>
                        </div>

                        {isSelectedRemote ? (
                          <div>
                            <label className="block text-[10px] text-slate-600 mb-1 font-bold">
                              Password
                            </label>
                            <input
                              type="password"
                              name="password"
                              id="ssh_password_input"
                              autoComplete="current-password"
                              required
                              value={sshPassword}
                              onChange={(e) => setSshPassword(e.target.value)}
                              placeholder="Enter SSH Password"
                              className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition"
                              autoFocus
                            />
                          </div>
                        ) : (
                          <div className="p-2 rounded-lg bg-slate-50 border border-slate-100 text-[10px] text-slate-500 flex items-center gap-1.5">
                            <CheckCircle2 size={12} className="text-emerald-500" />
                            <span>Instance locale / conteneurisé (aucun mot de passe requis).</span>
                          </div>
                        )}
                      </div>

                      {/* HIGH-END GRANULAR RESULT ALERT */}
                      {deployResult && (
                        <div
                          className={`p-4 rounded-2xl border text-xs shadow-2xs space-y-2.5 animate-fadeIn ${
                            deployResult.status === 'uptodate'
                              ? 'bg-sky-50/80 text-sky-950 border-sky-200'
                              : deployResult.success
                              ? 'bg-emerald-50/80 text-emerald-950 border-emerald-200'
                              : 'bg-rose-50/80 text-rose-950 border-rose-200'
                          }`}
                        >
                          <div className="flex items-start gap-2.5">
                            <div className={`p-1.5 rounded-xl shrink-0 mt-0.5 ${
                              deployResult.status === 'uptodate'
                                ? 'bg-sky-100 text-sky-600'
                                : deployResult.success
                                ? 'bg-emerald-100 text-emerald-600'
                                : 'bg-rose-100 text-rose-600'
                            }`}>
                              {deployResult.status === 'uptodate' ? (
                                <CheckCircle2 size={16} />
                              ) : deployResult.success ? (
                                <CheckCircle2 size={16} />
                              ) : (
                                <AlertCircle size={16} />
                              )}
                            </div>

                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between gap-2">
                                <div className="font-black text-xs uppercase tracking-wide">
                                  {deployResult.status === 'uptodate'
                                    ? 'Ressources Déjà Synchronisées'
                                    : deployResult.success
                                    ? 'Déploiement Terminé avec Succès'
                                    : 'Échec de la Synchronisation SSH'}
                                </div>
                                <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-md border shrink-0 ${
                                  deployResult.status === 'uptodate'
                                    ? 'bg-sky-100 text-sky-700 border-sky-300'
                                    : deployResult.success
                                    ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
                                    : 'bg-rose-100 text-rose-700 border-rose-300'
                                }`}>
                                  {deployResult.status === 'uptodate'
                                    ? '⚡ 0 modif'
                                    : deployResult.success
                                    ? '✓ Réussi'
                                    : '✕ Erreur'}
                                </span>
                              </div>

                              <p className="text-[11px] mt-0.5 opacity-90 leading-relaxed font-medium">
                                {deployResult.message}
                              </p>

                              {/* Detailed Breakdown per school / instance (Full, non-truncated readable card) */}
                              {deployResult.results && deployResult.results.length > 0 && (
                                <div className="space-y-2 mt-2.5 pt-2 border-t border-black/5">
                                  {deployResult.results.map((r, i) => {
                                    const isItemUpToDate = r.status === 'uptodate';
                                    const isItemSuccess = r.status === 'success';

                                    return (
                                      <div
                                        key={i}
                                        className="bg-white/95 p-3 rounded-xl border border-black/5 shadow-2xs space-y-1"
                                      >
                                        <div className="flex items-center justify-between gap-2">
                                          <div className="flex items-center gap-1.5 font-bold text-slate-800 text-xs">
                                            <Building2 size={13} className="text-slate-500 shrink-0" />
                                            <span>{r.name}</span>
                                            <span className="text-[10px] text-slate-400 font-mono font-normal">({r.vpsIp})</span>
                                          </div>

                                          <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-md border shrink-0 ${
                                            isItemUpToDate
                                              ? 'bg-sky-50 text-sky-700 border-sky-200'
                                              : isItemSuccess
                                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                              : 'bg-rose-50 text-rose-700 border-rose-200'
                                          }`}>
                                            {isItemUpToDate ? '⚡ À jour' : isItemSuccess ? '✓ Déployé' : '✕ Erreur'}
                                          </span>
                                        </div>

                                        <div className="text-xs text-slate-600 font-normal leading-relaxed pt-0.5">
                                          {r.details}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Drawer Bottom Sticky Action Bar */}
                    <div className="p-4 bg-white border-t border-slate-200 flex items-center justify-end gap-2 shrink-0 shadow-lg">
                      <button
                        type="button"
                        onClick={() => setDeployModalOpen(false)}
                        className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition"
                      >
                        Fermer
                      </button>
                      <button
                        type="submit"
                        disabled={
                          (isSelectedRemote && !sshPassword) ||
                          (deployScope === 'current' && folderSubScope === 'custom' && selectedSubItemPaths.length === 0)
                        }
                        className="flex-1 flex justify-center items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition disabled:opacity-50 shadow-md shadow-indigo-500/20"
                      >
                        <Send size={13} />
                        <span>
                          {vpsTargetMode === 'all_on_vps' && tenantsOnSameVps.length > 1
                            ? `Lancer pour les ${tenantsOnSameVps.length} Écoles`
                            : 'Lancer la Synchronisation SSH'}
                        </span>
                      </button>
                    </div>
                  </form>
                )}
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Floating Notification Toast */}
      {notification && (
        <div className="fixed bottom-6 right-6 z-50 animate-fadeIn">
          <div
            className={`px-4 py-2.5 rounded-lg shadow-xl border text-xs font-bold flex items-center gap-2 text-white ${
              notification.type === 'error' ? 'bg-rose-600 border-rose-700' : 'bg-slate-900 border-slate-800'
            }`}
          >
            {notification.type === 'error' ? <AlertCircle size={14} /> : <CheckCircle2 size={14} className="text-emerald-400" />}
            <span>{notification.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default BibliothequeDeRessources;
